import Loading from "@components/common/loading/Loading.js";
import React, { useRef, useEffect } from "react";
import { useTransition, animated } from "react-spring";
import { VariableSizeList as List } from "react-window";

import NoteItem from "./NoteItem.js";
import NapTime from "../../../assets/imgs/otto-scribbles-sleep.svg";
import LoadMore from "../../common/inbox/LoadMore";
import { getNoteItemSize } from "../../inbox/utils.js";

function EmptyState() {
  return (
    <div className="p-10 h-full flex">
      <div className="flex flex-col self-center justify-self-center space-y-5">
        <img
          className="flex self-center"
          width="250px"
          src={NapTime}
        />

        <h4 className="text-center text-sm text-gray-500 dark:text-gray-400">
          Take a paws...
        </h4>
        <h5
          className="text-center text-xs text-gray-400 dark:text-gray-500"
          data-testid="empty-inbox-message"
        >
          There are no new notes for you to catch up on for now. Go on
          and enjoy the rest of your day! <br /> (and let Otto and
          Scribbles catch some Z&apos;s)
        </h5>
      </div>
    </div>
  );
}

function NoteList(props) {
  const {
    items,
    incrementOffset,
    loadingMore,
    totalNotes,
    handleActionWithPrevention,
    loading,
    dataTestId,
  } = props;
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef?.current) {
      listRef?.current.resetAfterIndex(0, true);
    }
  }, [items]);

  const getItemSize = (index) => getNoteItemSize(items, index);

  const fadeAnim = useTransition(loading, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const renderNoteItem = ({ index, style }) => {
    const note = items[index];
    const itemStyle = {
      ...style,
      height: style?.height,
    };

    if (index == items.length && totalNotes <= items.length) {
      // Only grow the index if there are more notes to load
      return null;
    }

    if (index == items.length && totalNotes > items.length) {
      return (
        <LoadMore
          itemStyle={itemStyle}
          incrementOffset={incrementOffset}
          loadingMore={loadingMore}
        />
      );
    }

    return (
      <div style={itemStyle} className="pt-4">
        <NoteItem
          handleActionWithPrevention={handleActionWithPrevention}
          dashboard={true}
          note={note}
          {...props}
          dataTestIdUuid={`note-item-${note.uuid}`}
          dataTestIdTemplate="note-template-type"
          dataTestIdNoteTitle={`note-item-title-${note.title}`}
        />
      </div>
    );
  };

  return (
    <>
      {fadeAnim((styles, item) =>
        !item ? (
          <div
            className="relative h-full w-full"
            data-testid={dataTestId}
          >
            <div className="z-10 bg-gradient-to-t from-white via-white dark:from-gray-800 dark:via-gray-800 absolute bottom-0 w-full flex justify-center pt-10 px-5"></div>
            {!items?.length ? (
              <div className="w-[85%] h-[85%] mx-auto">
                <EmptyState />
              </div>
            ) : (
              <div className="relative flex flex-col justify-center items-center w-fulls">
                <List
                  ref={listRef}
                  height={400}
                  width="96%"
                  // When infinite scrolling is implemented,
                  // this will need to be updated to the total number of notes (no + 1).
                  // The plus one is only here to account for the "Load More" button
                  itemCount={items?.length + 1}
                  itemSize={getItemSize}
                >
                  {renderNoteItem}
                </List>
              </div>
            )}
          </div>
        ) : (
          <animated.div
            style={styles}
            className="absolute flex flex-col h-full min-h-[400px] min-w-full place-content-center z-10 top-0"
          >
            <Loading shouldShow={true} />
            <h4 className="mt-10 text-center text-base text-gray-500 dark:text-gray-400">
              Loading...
            </h4>
          </animated.div>
        ),
      )}
    </>
  );
}

export default NoteList;
